import { computed, inject, provide, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { formatPeriodLabel } from './formatters';

export const MARKETING_FILTERS_KEY = 'marketingFilters';

export const SHARED_KEYS = [
  'product_id',
  'marke_id',
  'project_id',
  'campaign_id',
  'measure_id',
  'channel',
  'placement_type',
  'status',
  'period',
  'date_from',
  'date_to',
  'year',
  'month',
  'quarter'
];

export const PAGE_EXTRAS = {
  'marketing-email': ['mailing_id'],
  'marketing-digital': ['website_id'],
  'marketing-social': ['platform', 'content_type', 'spend_type'],
  'marketing-traffic': ['source_type', 'source_label', 'landing_page'],
  'marketing-print': ['title_id', 'print_status']
};

const ALL_PAGE_EXTRAS = [...new Set(Object.values(PAGE_EXTRAS).flat())];

// UX: Einheitlicher Standard-Zeitraum auf allen Seiten. Vorher startete "Digitale
// Kampagnen" mit "Letzte 30 Tage" und zeigte eine leere Seite, Übersicht und
// Print dagegen mit dem Jahr. Jetzt gilt überall das aktuelle Jahr.
function defaultTimeFilter() {
  return { year: String(new Date().getFullYear()) };
}

// UX: Globale Filter nur dort zeigen, wo sie sinnvoll sind. "Kanal" ist auf einer
// Kanalseite redundant, "Schaltungstyp" gibt es bei E-Mail, Social und Traffic nicht.
export const HIDDEN_GLOBAL_FILTERS = {
  'marketing-overview': [],
  'marketing-digital': ['channel'],
  'marketing-print': ['channel'],
  'marketing-email': ['channel', 'placement_type'],
  'marketing-social': ['channel', 'placement_type'],
  'marketing-traffic': ['channel', 'placement_type']
};

export function isFilterVisible(routeName, key) {
  return !(HIDDEN_GLOBAL_FILTERS[routeName] || []).includes(key);
}

function emptyFilters() {
  return SHARED_KEYS.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {});
}

function queryValue(query, key) {
  const value = query[key];
  if (value === undefined || value === null || value === '') return '';
  return Array.isArray(value) ? String(value[0] ?? '') : String(value);
}

function fromQuery(query) {
  const filters = emptyFilters();
  SHARED_KEYS.forEach((key) => {
    filters[key] = queryValue(query, key);
  });
  return filters;
}

function toQuery(filters, extra = {}) {
  const query = {};
  SHARED_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== '' && value !== null && value !== undefined) {
      query[key] = String(value);
    }
  });
  Object.entries(extra).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return;
    if (Array.isArray(value)) {
      if (value.length) query[key] = value.join(',');
      return;
    }
    query[key] = String(value);
  });
  return query;
}

function extraFromRoute(query) {
  const extra = {};
  Object.keys(query || {}).forEach((key) => {
    if (!SHARED_KEYS.includes(key)) extra[key] = query[key];
  });
  return extra;
}

function hasTimeFilter(filters) {
  return Boolean(filters.period || filters.date_from || filters.date_to || filters.year);
}

function createMarketingFilters() {
  const route = useRoute();
  const router = useRouter();
  const filters = reactive(fromQuery(route.query));
  const pageFilters = reactive({ render: null, version: 0 });
  let pageFilterOwner = null;

  watch(
    () => route.query,
    (query) => {
      Object.assign(filters, fromQuery(query));
    }
  );

  const apiParams = computed(() => {
    const params = {};
    SHARED_KEYS.forEach((key) => {
      if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
        params[key] = filters[key];
      }
    });
    return params;
  });

  const activePeriodLabel = computed(() => formatPeriodLabel(filters));

  function replaceQuery(extra) {
    router.replace({ query: toQuery(filters, extra) });
  }

  function setFilter(patch) {
    Object.assign(filters, patch);
    if (patch.period) {
      filters.date_from = '';
      filters.date_to = '';
      filters.year = '';
      filters.month = '';
      filters.quarter = '';
    }
    // Nur ein tatsächlich eingetragenes Datum hebt den relativen Zeitraum auf. Leere Werte
    // kommen z. B. von der Schnellauswahl, die damit ein altes Von–bis löscht – vorher wurde
    // dadurch auch die gerade gewählte Schnellauswahl sofort wieder gelöscht.
    if (patch.date_from || patch.date_to) {
      filters.period = '';
    }
    if (patch.year !== undefined && patch.year !== '') {
      filters.period = '';
      if (patch.date_from === undefined) filters.date_from = '';
      if (patch.date_to === undefined) filters.date_to = '';
    }
    if (patch.month) {
      filters.quarter = '';
      filters.period = '';
    }
    if (patch.quarter) {
      filters.month = '';
      filters.period = '';
    }
    replaceQuery(extraFromRoute(route.query));
  }

  function setExtra(extraPatch) {
    replaceQuery({ ...extraFromRoute(route.query), ...extraPatch });
  }

  function extraValue(key, fallback = '') {
    const value = route.query[key];
    if (value === undefined || value === null || value === '') return fallback;
    if (Array.isArray(value)) return String(value[0] ?? fallback);
    return String(value);
  }

  // UX: Filter beim Seitenwechsel mitnehmen, aber ohne Filter, die auf der
  // Zielseite ausgeblendet sind. Sonst würde z. B. "Kanal: E-Mail" unsichtbar
  // die Digital-Seite leeren. Kanalspezifische Extras fallen ebenfalls weg.
  function queryFor(routeName) {
    const query = {};
    const hidden = new Set(HIDDEN_GLOBAL_FILTERS[routeName] || []);
    const keepExtras = new Set(PAGE_EXTRAS[routeName] || []);
    Object.entries(route.query).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) return;
      if (SHARED_KEYS.includes(key)) {
        if (!hidden.has(key)) query[key] = value;
        return;
      }
      if (!ALL_PAGE_EXTRAS.includes(key) || keepExtras.has(key)) query[key] = value;
    });
    return query;
  }

  function applyRouteDefaults() {
    if (hasTimeFilter(filters)) return;
    setFilter(defaultTimeFilter());
  }

  function clearPageExtras(routeName = route.name) {
    const keep = new Set(PAGE_EXTRAS[routeName] || []);
    const extra = extraFromRoute(route.query);
    let changed = false;
    ALL_PAGE_EXTRAS.forEach((key) => {
      if (!keep.has(key) && extra[key] != null && extra[key] !== '') {
        extra[key] = '';
        changed = true;
      }
    });
    if (changed) replaceQuery(extra);
  }

  function resetFilters() {
    const productId = filters.product_id;
    Object.assign(filters, emptyFilters());
    filters.product_id = productId;
    Object.assign(filters, defaultTimeFilter());
    replaceQuery({});
  }

  function setPageFilterSlot(fn, owner) {
    if (fn) {
      pageFilterOwner = owner;
      pageFilters.render = fn;
      pageFilters.version += 1;
      return;
    }
    if (pageFilterOwner !== owner) return;
    pageFilterOwner = null;
    pageFilters.render = null;
    pageFilters.version += 1;
  }

  return {
    filters,
    apiParams,
    activePeriodLabel,
    pageFilters,
    setFilter,
    setExtra,
    setPageFilterSlot,
    extraValue,
    queryFor,
    applyRouteDefaults,
    clearPageExtras,
    resetFilters
  };
}

export function provideMarketingFilters() {
  const store = createMarketingFilters();
  provide(MARKETING_FILTERS_KEY, store);
  return store;
}

export function useMarketingFilters() {
  const store = inject(MARKETING_FILTERS_KEY, null);
  if (!store) {
    throw new Error('useMarketingFilters() requires MarketingLayout.');
  }
  return store;
}
